import React, { useState } from "react";
import { ArrowRightIcon, CalendarClockIcon, FileTextIcon, Link2Icon, MailIcon, RefreshCwIcon, SendIcon, TagIcon, UploadIcon, UserRoundIcon, BoxIcon } from "lucide-react";
import { AudienceTag } from "./AudienceTag";
import { Avatar } from "./Avatar";
import { statusStyles } from "./StatusBadge";
import { formatDateTime, formatShortDate, getActor, getMember } from "../../utils/conditions";
import { ActivityEvent, ActivityKind, TimelineFilter } from "../../types/activity";
import { ConditionComment, ConditionStatus } from "../../types/conditions";
interface ActivityTimelineProps {
  comments: ConditionComment[];
  events: ActivityEvent[];
}
type TimelineItem = {
  type: 'comment';
  createdAt: string;
  comment: ConditionComment;
} | {
  type: 'event';
  createdAt: string;
  event: ActivityEvent;
};
const filters: {
  id: TimelineFilter;
  label: string;
}[] = [{
  id: 'all',
  label: 'All'
}, {
  id: 'comments',
  label: 'Comments'
}, {
  id: 'activity',
  label: 'Activity'
}, {
  id: 'borrower',
  label: 'Borrower-facing'
}];
const eventIcons: Record<ActivityKind, BoxIcon> = {
  status: RefreshCwIcon,
  eta: CalendarClockIcon,
  owner: UserRoundIcon,
  document_uploaded: UploadIcon,
  document_linked: Link2Icon,
  borrower_email: MailIcon,
  conditions_sent: SendIcon
};
export function ActivityTimeline({
  comments,
  events
}: ActivityTimelineProps) {
  const [filter, setFilter] = useState<TimelineFilter>('all');
  const items: TimelineItem[] = [...comments.map(comment => ({
    type: 'comment' as const,
    createdAt: comment.createdAt,
    comment
  })), ...events.map(event => ({
    type: 'event' as const,
    createdAt: event.createdAt,
    event
  }))].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const matches = (item: TimelineItem, f: TimelineFilter) => {
    if (f === 'comments') return item.type === 'comment';
    if (f === 'activity') return item.type === 'event';
    if (f === 'borrower') return item.type === 'comment' ? !!item.comment.sentToBorrower : !!item.event.borrowerFacing;
    return true;
  };
  const visible = items.filter(i => matches(i, filter));
  return <div className="flex min-h-0 flex-1 flex-col" data-id="element-3" data-mp="xmx3apkegrj4">
      <div className="flex flex-wrap gap-1.5 border-b border-slate-200 px-5 py-2.5" role="group" aria-label="Filter timeline" data-id="element-4" data-mp="jwjygw2t1sy0">
        {filters.map(f => {
        const active = filter === f.id;
        const count = items.filter(i => matches(i, f.id)).length;
        return <button key={f.id} type="button" aria-pressed={active} onClick={() => setFilter(f.id)} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand ${active ? 'bg-brand text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-5" data-mp="a077w72k3v7h">
              {f.label}
              <span className={`tabular-nums ${active ? 'text-white/80' : 'text-slate-400'}`} data-id="element-6" data-mp="1vbysps3mq6pf">{count}</span>
            </button>;
      })}
      </div>

      {visible.length === 0 ? <div className="flex flex-1 flex-col items-center justify-center px-8 py-12 text-center" data-id="element-7" data-mp="2af9zkap8ciof">
          <p className="text-sm font-semibold text-slate-800" data-id="element-8" data-mp="25g6z73k730ms">Nothing here yet</p>
          <p className="mt-1 text-sm text-slate-500" data-id="element-9" data-mp="2j6shor5ql8k5">
            {filter === 'borrower' ? 'No messages or emails have gone to the borrower for this condition.' : 'Add a note below to start the conversation.'}
          </p>
        </div> : <ol className="relative flex-1 overflow-y-auto px-5 py-4" aria-label="Condition activity, newest first" data-id="element-10" data-mp="2t3589g5zj6ao">
          <span className="absolute bottom-4 left-[31px] top-4 w-px bg-slate-200" aria-hidden="true" data-id="element-11" data-mp="2o5dfkjxing5f" />
          {visible.map(item => item.type === 'comment' ? <CommentItem key={item.comment.id} comment={item.comment} data-id="element-12" data-mp="2y39p57at231f" /> : <EventItem key={item.event.id} event={item.event} data-id="element-13" data-mp="2y1yp9ikhdffl" />)}
        </ol>}
    </div>;
}
function CommentItem({
  comment
}: {
  comment: ConditionComment;
}) {
  const author = getActor(comment.authorId);
  const toBorrower = !!comment.sentToBorrower;
  return <li className="relative flex gap-3 pb-5" data-id="element-14" data-mp="155w5yvx1wi7v">
      <span className="relative z-[1] rounded-full ring-4 ring-white" data-id="element-15" data-mp="159a39pquy18g">
        <Avatar member={author} size="sm" data-id="element-16" data-mp="1a47cawo50alc" />
      </span>
      <div className="min-w-0 flex-1" data-id="element-17" data-mp="1e7433antzd0d">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs" data-id="element-18" data-mp="1e85fbigl25jo">
          <span className="font-semibold text-slate-900" data-id="element-19" data-mp="1j5nkqyve62mx">{author.name}</span>
          <time className="text-slate-400" dateTime={comment.createdAt} data-id="element-20" data-mp="mgzhj8oc4jxk">
            {formatDateTime(comment.createdAt)}
          </time>
          <AudienceTag borrower={toBorrower} data-id="element-21" data-mp="qikq4htu6c1y" />
        </div>
        <div className={`mt-1.5 rounded-lg px-3 py-2 text-sm text-slate-800 ${toBorrower ? 'border-l-[3px] border-amber-400 bg-amber-50/70' : 'border border-slate-200 bg-white'}`} data-id="element-22" data-mp="vgm9l3v6cddy">
          <p className="whitespace-pre-wrap" data-id="element-23" data-mp="2sgscr3bx6iv">{comment.body}</p>
        </div>
        {comment.mentions.length > 0 && <p className="mt-1.5 flex flex-wrap items-center gap-1 text-xs text-slate-500" data-id="element-24" data-mp="7ra0s53re7c6">
            <TagIcon className="h-3 w-3" aria-hidden="true" data-id="element-25" data-mp="7sggpioxdek" />
            Tagged
            {comment.mentions.map(id => <span key={id} className="rounded bg-brand/10 px-1.5 py-0.5 font-medium text-brand-dark" data-id="element-26" data-mp="3rdtvta9out60">
                {getMember(id).name}
              </span>)}
          </p>}
      </div>
    </li>;
}
function EventItem({
  event
}: {
  event: ActivityEvent;
}) {
  const Icon = eventIcons[event.kind];
  const actor = getActor(event.actorId);
  const borrower = !!event.borrowerFacing;
  return <li className="relative flex gap-3 pb-4" data-id="element-27" data-mp="38nu7wqpl3k46">
      <span className={`relative z-[1] flex h-6 w-6 shrink-0 items-center justify-center rounded-full ring-4 ring-white ${borrower ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-28" data-mp="38n2gls30s8jp">
        <Icon className="h-3 w-3" aria-hidden="true" data-id="element-29" data-mp="2yqzayc8ylrs0" />
      </span>
      <div className="min-w-0 flex-1 pt-0.5 text-xs text-slate-600" data-id="element-30" data-mp="2uoksvud46w1s">
        <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1" data-id="element-31" data-mp="2upvxfwedw7ty">
          <EventSummary event={event} data-id="element-32" data-mp="2pphxl1mxs470" />
          {borrower && <AudienceTag borrower data-id="element-33" data-mp="2g0um7z4ywgiy" />}
        </p>
        <p className="mt-0.5 text-slate-400" data-id="element-34" data-mp="2fzt0n0qbsbmz">
          {actor.name} · <time dateTime={event.createdAt} data-id="element-35" data-mp="2k37pmgw8s2ck">{formatDateTime(event.createdAt)}</time>
        </p>
        {event.fileNames && event.fileNames.length > 0 && <ul className="mt-1.5 flex flex-wrap gap-1.5" data-id="element-36" data-mp="2650yrkppn2me">
            {event.fileNames.map(name => <li key={name} className="inline-flex max-w-full items-center gap-1 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] text-slate-700" data-id="element-37" data-mp="2b2t178nze74u">
                <FileTextIcon className="h-3 w-3 shrink-0 text-red-500" aria-hidden="true" data-id="element-38" data-mp="33r83cum0kuyr" />
                <span className="truncate" data-id="element-39" data-mp="2p2kd93uxtdg7">{name}</span>
              </li>)}
          </ul>}
      </div>
    </li>;
}
function EventSummary({
  event
}: {
  event: ActivityEvent;
}) {
  const strong = 'font-medium text-slate-800';
  const arrow = <ArrowRightIcon className="h-3 w-3 text-slate-400" aria-hidden="true" data-id="element-40" data-mp="3hp53g0zofji6" />;
  const n = event.fileNames?.length ?? 0;
  switch (event.kind) {
    case 'status':
      return <>
          <span data-id="element-41" data-mp="1b1flj7uu11oi">Status:</span>
          <span className={strong} data-id="element-42" data-mp="1b0miagv30ub4">{statusStyles[(event.from ?? 'none') as ConditionStatus].label}</span>
          {arrow}
          <span className={strong} data-id="element-43" data-mp="1jurihqw3vi3b">{statusStyles[(event.to ?? 'none') as ConditionStatus].label}</span>
        </>;
    case 'eta':
      if (!event.to) return <span data-id="element-44" data-mp="1fuh2dlelnbaq">ETA cleared</span>;
      return event.from ? <>
          <span data-id="element-45" data-mp="s3bq1g7vb156">ETA:</span>
          <span className={strong} data-id="element-46" data-mp="s4mpj3ua5mqh">{formatShortDate(event.from)}</span>
          {arrow}
          <span className={strong} data-id="element-47" data-mp="n5a6yvafz1tp">{formatShortDate(event.to)}</span>
        </> : <>
          <span data-id="element-48" data-mp="114t0ahylg8ly">ETA set to</span>
          <span className={strong} data-id="element-49" data-mp="1152r27rgqjsp">{formatShortDate(event.to)}</span>
        </>;
    case 'owner':
      return <>
          <span data-id="element-50" data-mp="w3vkud4yxso5">Owner:</span>
          <span className={strong} data-id="element-51" data-mp="2cjg9uy7d091v">{event.from ? getMember(event.from).name : 'Unassigned'}</span>
          {arrow}
          <span className={strong} data-id="element-52" data-mp="27kvaf0zxlteh">{event.to ? getMember(event.to).name : 'Unassigned'}</span>
        </>;
    case 'document_uploaded':
      return <span data-id="element-53" data-mp="2ro17rm1easy0">
          Uploaded <span className={strong} data-id="element-54" data-mp="2rkdgktjfaimg">{n} {n === 1 ? 'document' : 'documents'}</span>
        </span>;
    case 'document_linked':
      return <span data-id="element-55" data-mp="30neggchp3dho">
          Linked <span className={strong} data-id="element-56" data-mp="30m3g6mngknvb">{n} existing {n === 1 ? 'document' : 'documents'}</span>
        </span>;
    case 'borrower_email':
      return <span data-id="element-57" data-mp="35fza9e0lpql7">Condition request emailed to borrower</span>;
    case 'conditions_sent':
      return <span data-id="element-58" data-mp="3acpr43k8cq7o">Included in conditions email to borrower</span>;
  }
}